export type MessageToolbarPreset = "none" | "basic" | "developer";

export type MessageToolbarTool =
  | "bold"
  | "italic"
  | "strike"
  | "inlineCode"
  | "bulletList"
  | "orderedList"
  | "codeBlock"
  | "link";

export interface MessageFeatureFlags {
  mentions: boolean;
  emojiReplacement: boolean;
  internalLinkResolution: boolean;
  documentEvidence: boolean;
}

export type MessageEnterKeyBehavior =
  | "enterToSend"
  | "enterToSendUnlessMultiline"
  | "enterToNewline";

const MESSAGE_TOOLBAR_TOOL_ORDER: MessageToolbarTool[] = [
  "bold",
  "italic",
  "strike",
  "inlineCode",
  "bulletList",
  "orderedList",
  "codeBlock",
  "link",
];

const MESSAGE_TOOLBAR_PRESETS: Record<MessageToolbarPreset, MessageToolbarTool[]> = {
  none: [],
  basic: ["bold", "italic", "bulletList", "orderedList"],
  developer: [
    "bold",
    "italic",
    "strike",
    "inlineCode",
    "bulletList",
    "orderedList",
    "codeBlock",
    "link",
  ],
};

const MESSAGE_TOOLBAR_TOOL_SET = new Set<MessageToolbarTool>(MESSAGE_TOOLBAR_TOOL_ORDER);

const DEFAULT_MESSAGE_FEATURE_FLAGS: MessageFeatureFlags = {
  mentions: true,
  emojiReplacement: true,
  internalLinkResolution: true,
  documentEvidence: false,
};

const DEFAULT_MESSAGE_ENTER_KEY_BEHAVIOR: MessageEnterKeyBehavior = "enterToSendUnlessMultiline";

interface ResolveToolbarToolsOptions {
  toolbarPreset?: MessageToolbarPreset;
  toolbarTools?: MessageToolbarTool[] | null;
}

export function resolveMessageFeatureFlags(
  overrides?: Partial<MessageFeatureFlags>
): MessageFeatureFlags {
  return {
    ...DEFAULT_MESSAGE_FEATURE_FLAGS,
    ...overrides,
  };
}

export function resolveToolbarTools(options?: ResolveToolbarToolsOptions): MessageToolbarTool[] {
  const explicitTools = options?.toolbarTools;
  if (Array.isArray(explicitTools)) {
    const normalizedTools = explicitTools.filter((tool): tool is MessageToolbarTool =>
      MESSAGE_TOOLBAR_TOOL_SET.has(tool)
    );

    return MESSAGE_TOOLBAR_TOOL_ORDER.filter((tool) => normalizedTools.includes(tool));
  }

  const preset = options?.toolbarPreset ?? "basic";
  return [...MESSAGE_TOOLBAR_PRESETS[preset]];
}

export function resolveEnterKeyBehavior(
  behavior?: MessageEnterKeyBehavior
): MessageEnterKeyBehavior {
  return behavior ?? DEFAULT_MESSAGE_ENTER_KEY_BEHAVIOR;
}

interface ShouldSubmitOnEnterOptions {
  behavior: MessageEnterKeyBehavior;
  hasModifier: boolean;
  shiftKey: boolean;
  isMultiline: boolean;
}

export function shouldUseRealNewlineOnShiftEnter(behavior: MessageEnterKeyBehavior): boolean {
  return behavior === "enterToSend" || behavior === "enterToSendUnlessMultiline";
}

export function shouldSubmitOnEnter(options: ShouldSubmitOnEnterOptions): boolean {
  if (options.hasModifier) return true;

  switch (options.behavior) {
    case "enterToSend":
      return !options.shiftKey;
    case "enterToSendUnlessMultiline":
      if (options.shiftKey) return false;
      return !options.isMultiline;
    case "enterToNewline":
      return false;
  }
}
